import React from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { LogoutIcon } from './Icons';
import NotificationBell from './NotificationBell';
import { db } from '../firebase';
// ✅ STEP 1: Get the full auth state from the hook
import { useAuth } from '../hooks/useAuth';

export default function Header({ onLogout }) {
    // ✅ Get profile, loading, and validation status
    const { profile, loading: authLoading, validationStatus } = useAuth();
    const navigate = useNavigate();
    const { pathname } = useLocation();

    const handleSignOut = async () => {
        try {
            if (onLogout) {
                await onLogout();
            }
        } catch (error) {
            console.error("Error signing out: ", error);
        }
    };

    const navItems = [
        { name: 'home', label: 'Início', path: '/home' },
        { name: 'market', label: 'Ver Itens', path: '/market' },
        { name: 'trades', label: 'Trocas', path: '/trades' },
        { name: 'profile', label: 'Perfil', path: '/profile' },
    ];

    // ✅ STEP 2: Implement the robust Guard Clause
    // This prevents the Header and its children (like NotificationBell)
    // from rendering until the user is fully authenticated and validated.
    if (authLoading || validationStatus !== 'success' || !profile) {
        return null; // Render nothing while loading or if the user is not valid
    }

    return (
        <header className="hidden md:flex bg-white shadow-md sticky top-0 z-40 items-center justify-between">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-16">
                    <div className="flex items-center">
                        {/* Logo */}
                        <Link to="/home" className="text-2xl font-bold text-green-700">Escambo Digital</Link>
                        
                        {/* Main navigation for regular users */}
                        {profile.role !== 'admin' && (
                            <div className="ml-10 flex items-baseline space-x-4">
                                {navItems.map(item => (
                                    <Link
                                        key={item.name}
                                        to={item.path}
                                        className={`px-3 py-2 rounded-md text-sm font-medium ${
                                            pathname === item.path
                                                ? 'bg-green-100 text-green-700'
                                                : 'text-gray-500 hover:bg-gray-100 hover:text-gray-900'
                                        }`}
                                    >
                                        {item.label}
                                    </Link>
                                ))}
                            </div>
                        )}
                    </div>
                    
                    {/* Actions on the right */}
                    <div className="flex items-center">
                        {/* Admin Panel button */}
                        {profile.role === 'admin' && (
                            <Link to="/admin" className="mr-4 bg-purple-600 text-white px-4 py-2 rounded-lg shadow hover:bg-purple-700 transition text-sm font-medium">
                                Painel Admin
                            </Link>
                        )}
                        
                        {/* "Anunciar Item" button for regular users */}
                        {profile.role !== 'admin' && (
                            <button
                                onClick={() => navigate('/add-item')}
                                className="mr-4 bg-green-600 text-white px-4 py-2 rounded-lg shadow hover:bg-green-700 transition text-sm font-medium"
                            >
                                Anunciar Item
                            </button>
                        )}

                        {/* Credit balance for regular users */}
                        {profile.role !== 'admin' && (
                            <div className="text-right mr-4">
                                <p className="font-bold text-gray-800">{profile.credits || 0} Créditos</p>
                                <p className="text-xs text-gray-500">Seu Saldo</p>
                            </div>
                        )}

                        {/* Notification Bell for regular users */}
                        {profile.role !== 'admin' && (
                           <NotificationBell db={db} />
                        )}
                        
                        {/* Logout button */}
                        <button onClick={handleSignOut} title="Sair" className="p-2 rounded-full text-gray-500 hover:bg-red-100 hover:text-red-600">
                            <LogoutIcon className="w-6 h-6" />
                        </button>
                    </div>
                </div>
            </div>
        </header>
    );
}

